import React, { useState } from "react";
import styled from "styled-components";
import LazyImage from "./LazyImage";

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

const GalleryItem = styled.div`
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const StyledLazyImage = styled(LazyImage)`
  width: 100%;
  height: 100%;
  border-radius: 12px;

  img {
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const LightboxOverlay = styled.div`
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  flex-direction: column;
  text-align: center;
  backdrop-filter: blur(10px);
`;

const LightboxContent = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  margin-bottom: 1rem;
`;

const LightboxDescription = styled.p`
  color: white;
  font-size: 1.25rem;
  margin: 1rem 0;
  max-width: 600px;
  line-height: 1.5;
`;

const LightboxMetadata = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 2rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === "prev" ? "left: 2rem;" : "right: 2rem;")}
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.5rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  backdrop-filter: blur(10px);
`;

const ErrorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  background-color: #f5f5f5;
  border: 2px dashed #ddd;
  border-radius: 12px;
`;

function Gallery({ images = [] }) {
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const openLightbox = (index) => {
		setSelectedIndex(index);
		setLightboxOpen(true);
		// Prevent body scroll when lightbox is open
		document.body.style.overflow = "hidden";
	};

	const closeLightbox = React.useCallback(() => {
		setLightboxOpen(false);
		setSelectedIndex(0);
		// Restore body scroll
		document.body.style.overflow = "unset";
	}, []);

	const goToPrevious = React.useCallback(() => {
		setSelectedIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
	}, [images.length]);

	const goToNext = React.useCallback(() => {
		setSelectedIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
	}, [images.length]);

	const handleKeyDown = React.useCallback(
		(event) => {
			if (!lightboxOpen) return;

			switch (event.key) {
				case "Escape":
					closeLightbox();
					break;
				case "ArrowLeft":
					goToPrevious();
					break;
				case "ArrowRight":
					goToNext();
					break;
				default:
					break;
			}
		},
		[lightboxOpen, closeLightbox, goToPrevious, goToNext],
	);

	// Add keyboard event listener
	React.useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			// Cleanup: restore body scroll on unmount
			document.body.style.overflow = "unset";
		};
	}, [handleKeyDown]);

	if (!images || images.length === 0) {
		return (
			<div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
				No images to display
			</div>
		);
	}

	const selectedImage = images[selectedIndex];

	return (
		<>
			<GalleryGrid>
				{images.map((item, index) => (
					<GalleryItem
						key={item.id || index}
						onClick={() => openLightbox(index)}
					>
						{item.src && item.isLoaded !== false ? (
							<StyledLazyImage
								src={item.src}
								alt={item.alt || item.description || `Project ${index + 1}`}
								loadingThreshold={0.1}
							/>
						) : (
							<ErrorPlaceholder>
								<div>⚠️</div>
								<div>Failed to load image</div>
								{item.filename && <small>{item.filename}</small>}
							</ErrorPlaceholder>
						)}
					</GalleryItem>
				))}
			</GalleryGrid>

			{/* Lightbox */}
			{selectedImage && (
				<LightboxOverlay
					isOpen={lightboxOpen}
					onClick={(e) => {
						// Only close if clicking on the overlay, not the content
						if (e.target === e.currentTarget) {
							closeLightbox();
						}
					}}
				>
					<CloseButton onClick={closeLightbox} title="Close (Esc)">
						×
					</CloseButton>

					{images.length > 1 && (
						<>
							<NavigationButton
								direction="prev"
								onClick={goToPrevious}
								title="Previous image (←)"
							>
								←
							</NavigationButton>
							<NavigationButton
								direction="next"
								onClick={goToNext}
								title="Next image (→)"
							>
								→
							</NavigationButton>
						</>
					)}

					<LightboxContent>
						{selectedImage.src ? (
							<LightboxImage
								src={selectedImage.src}
								alt={
									selectedImage.alt ||
									selectedImage.description ||
									"Enlarged view"
								}
							/>
						) : (
							<ErrorPlaceholder style={{ width: "400px", height: "300px" }}>
								<div>⚠️</div>
								<div>Image not available</div>
							</ErrorPlaceholder>
						)}

						{selectedImage.description && (
							<LightboxDescription>
								{selectedImage.description}
							</LightboxDescription>
						)}

						<LightboxMetadata>
							{selectedImage.category && (
								<span>Category: {selectedImage.category}</span>
							)}
							{selectedImage.tags && selectedImage.tags.length > 0 && (
								<span>Tags: {selectedImage.tags.join(", ")}</span>
							)}
						</LightboxMetadata>
					</LightboxContent>

					{images.length > 1 && (
						<ImageCounter>
							{selectedIndex + 1} of {images.length}
						</ImageCounter>
					)}
				</LightboxOverlay>
			)}
		</>
	);
}

export default Gallery;
